/**
 * Admin Job Service Triggers Panel
 * Manage service trigger rules and saved search alert settings
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Zap,
  Bell,
  Settings,
  RefreshCw,
  Save,
  Loader2,
  Target,
  Mail,
  Clock,
  BarChart3,
  Play,
  Pause,
  Eye,
  FileText,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;


// ==================== TRIGGER RULES TAB ====================
const TriggerRulesTab = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState({});
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  
  useEffect(() => {
    fetchRules();
    fetchStats();
  }, []);
  
  const fetchRules = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/job-service-triggers/admin/rules`);
      if (response.data.success) {
        setRules(response.data.rules || {});
      }
    } catch (error) {
      console.error('Error fetching rules:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/job-service-triggers/admin/stats?days=30`);
      if (response.data.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };
  
  const updateRule = async (ruleId, updates) => {
    try {
      setSaving(true);
      const response = await axios.put(
        `${API_URL}/api/job-service-triggers/admin/rules/${ruleId}`,
        updates
      );
      
      if (response.data.success) {
        setRules(prev => ({
          ...prev,
          [ruleId]: { ...prev[ruleId], ...updates },
        }));
        toast({
          title: 'Rule Updated',
          description: `Trigger rule "${ruleId}" has been updated`,
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update rule',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };
  
  const resetRules = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/job-service-triggers/admin/rules/reset`);
      if (response.data.success) {
        setRules(response.data.rules);
        toast({
          title: 'Rules Reset',
          description: 'All trigger rules have been reset to defaults',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to reset rules',
        variant: 'destructive',
      });
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <BarChart3 className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_events || 0}</p>
                  <p className="text-sm text-gray-500">Events (30 days)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Eye className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.events_by_type?.view_jobs || 0}</p>
                  <p className="text-sm text-gray-500">Job Views</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Target className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{Object.keys(rules).length}</p>
                  <p className="text-sm text-gray-500">Active Rules</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Actions */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Trigger Rules</h3>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchRules}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={resetRules}>
            Reset to Defaults
          </Button>
        </div>
      </div>
      
      {/* Rules List */}
      <div className="space-y-4">
        {Object.entries(rules).map(([ruleId, rule]) => (
          <Card key={ruleId} className={!rule.enabled ? 'opacity-60' : ''}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="font-medium">{rule.name}</h4>
                    <Badge variant={rule.enabled ? 'default' : 'secondary'}>
                      {rule.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                    <Badge variant="outline">Priority: {rule.priority}</Badge>
                  </div>
                  <p className="text-sm text-gray-500 mb-3">{rule.description}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">Trigger Message</Label>
                      <Input
                        value={rule.message || ''}
                        onChange={(e) => {
                          setRules(prev => ({
                            ...prev,
                            [ruleId]: { ...prev[ruleId], message: e.target.value },
                          }));
                        }}
                        onBlur={() => updateRule(ruleId, { message: rule.message })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Threshold</Label>
                      <Input
                        type="number"
                        value={rule.threshold || 1}
                        onChange={(e) => {
                          setRules(prev => ({
                            ...prev,
                            [ruleId]: { ...prev[ruleId], threshold: parseInt(e.target.value) },
                          }));
                        }}
                        onBlur={() => updateRule(ruleId, { threshold: rule.threshold })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <Label className="text-xs">Services to Show</Label>
                    <p className="text-sm mt-1">{rule.services?.join(', ') || 'None'}</p>
                  </div>
                </div>
                
                <div className="ml-4">
                  <Switch
                    checked={rule.enabled}
                    onCheckedChange={(checked) => updateRule(ruleId, { enabled: checked })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};


// ==================== SAVED SEARCH ALERTS TAB ====================
const SavedSearchAlertsTab = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  
  useEffect(() => {
    fetchSettings();
    fetchStats();
    fetchLogs();
  }, []);
  
  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/saved-search-alerts/admin/settings`);
      if (response.data.success) {
        setSettings(response.data.settings || {});
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/saved-search-alerts/admin/stats?days=30`);
      if (response.data.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };
  
  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/saved-search-alerts/admin/logs?limit=20`);
      if (response.data.success) {
        setLogs(response.data.logs || []);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };
  
  const saveSettings = async () => {
    try {
      setSaving(true);
      const response = await axios.put(
        `${API_URL}/api/saved-search-alerts/admin/settings`,
        settings
      );
      
      if (response.data.success) {
        toast({
          title: 'Settings Saved',
          description: 'Alert settings have been updated',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };
  
  const runAlerts = async (frequency) => {
    try {
      setRunning(true);
      const response = await axios.post(
        `${API_URL}/api/saved-search-alerts/run?frequency=${frequency}`
      );
      
      if (response.data.success) {
        toast({
          title: 'Alerts Triggered',
          description: `${frequency} alerts are being processed`,
        });
        // Refresh logs after a delay
        setTimeout(fetchLogs, 3000);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to run alerts',
        variant: 'destructive',
      });
    } finally {
      setRunning(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Mail className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_alerts_sent || 0}</p>
                  <p className="text-sm text-gray-500">Alerts Sent (30d)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <FileText className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_jobs_matched || 0}</p>
                  <p className="text-sm text-gray-500">Jobs Matched</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Bell className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.active_saved_searches || 0}</p>
                  <p className="text-sm text-gray-500">Active Searches</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${settings.enabled ? 'bg-green-100' : 'bg-red-100'}`}>
                  {settings.enabled ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                  )}
                </div>
                <div>
                  <p className="text-2xl font-bold">{settings.enabled ? 'ON' : 'OFF'}</p>
                  <p className="text-sm text-gray-500">Alerts Status</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Alert Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium">Enable Email Alerts</p>
              <p className="text-sm text-gray-500">Send automated job alerts to users</p>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Max Jobs Per Email</Label>
              <Input
                type="number"
                value={settings.max_jobs_per_email || 10}
                onChange={(e) => setSettings(prev => ({ ...prev, max_jobs_per_email: parseInt(e.target.value) }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Daily Send Hour (24h)</Label>
              <Input
                type="number"
                min="0"
                max="23"
                value={settings.daily_send_hour || 9}
                onChange={(e) => setSettings(prev => ({ ...prev, daily_send_hour: parseInt(e.target.value) }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Weekly Send Day</Label>
              <Select
                value={String(settings.weekly_send_day || 1)}
                onValueChange={(v) => setSettings(prev => ({ ...prev, weekly_send_day: parseInt(v) }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Monday</SelectItem>
                  <SelectItem value="1">Tuesday</SelectItem>
                  <SelectItem value="2">Wednesday</SelectItem>
                  <SelectItem value="3">Thursday</SelectItem>
                  <SelectItem value="4">Friday</SelectItem>
                  <SelectItem value="5">Saturday</SelectItem>
                  <SelectItem value="6">Sunday</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div>
            <Label>Email Subject Template</Label>
            <Input
              value={settings.subject_template || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, subject_template: e.target.value }))}
              placeholder="🔔 {count} new jobs match your saved search '{name}'"
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">Use {'{count}'} and {'{name}'} as placeholders</p>
          </div>
          
          <div className="flex gap-2 pt-4">
            <Button onClick={saveSettings} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Settings
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Manual Run */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Play className="h-5 w-5" />
            Manual Alert Run
          </CardTitle>
          <CardDescription>
            Manually trigger alert emails for testing or catch-up
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <Button
              variant="outline"
              onClick={() => runAlerts('daily')}
              disabled={running}
            >
              {running ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Clock className="h-4 w-4 mr-2" />}
              Run Daily Alerts
            </Button>
            <Button
              variant="outline"
              onClick={() => runAlerts('weekly')}
              disabled={running}
            >
              {running ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Clock className="h-4 w-4 mr-2" />}
              Run Weekly Alerts
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Recent Logs */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Recent Alert Logs</CardTitle>
            <Button variant="outline" size="sm" onClick={fetchLogs}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No alert logs yet</p>
          ) : (
            <div className="space-y-2">
              {logs.map((log, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {log.email_sent ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                    )}
                    <div>
                      <p className="text-sm font-medium">{log.user_email}</p>
                      <p className="text-xs text-gray-500">
                        {log.jobs_count} jobs matched • {log.search_id}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400">
                    {new Date(log.created_at).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


// ==================== MAIN COMPONENT ====================
const AdminJobServiceTriggers = () => {
  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-job-triggers">
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Zap className="h-6 w-6 text-blue-600" />
          Job Service Triggers & Alerts
        </h1>
        <p className="text-gray-500 mt-1">
          Configure service ad triggers and manage saved search email alerts
        </p>
      </div>
      
      <Tabs defaultValue="triggers" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="triggers" className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            Service Triggers
          </TabsTrigger>
          <TabsTrigger value="alerts" className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Saved Search Alerts
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="triggers">
          <TriggerRulesTab />
        </TabsContent>
        
        <TabsContent value="alerts">
          <SavedSearchAlertsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};


export default AdminJobServiceTriggers;
